import { useState } from "react";
import { ListGroup, Form, Button, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import Loader from "./Loader";
import Message from "./Message";
import Rating from "./Rating";
import {
  useCreateOrderReviewMutation,
  useGetOrderReviewByIdQuery
} from "../slices/ordersApiSlice";
import { dateFormatter } from "../utils/dateHelper";

const OrderReview = ({ order, orderId, refetch }) => {
  const [orderRating, setOrderRating] = useState(0);
  const [orderComment, setOrderComment] = useState("");
  const [savingOrderReview, setSavingOrderReview] = useState(false);

  const {
    data: orderReview,
    isLoading: isLoadingGetOrderReview,
    refetch: refetchGetOrderReview
  } = useGetOrderReviewByIdQuery(orderId);

  const [createOrderReview, { isLoading: isLoadingOrderReview }] =
    useCreateOrderReviewMutation();

  const handleOrderReviewSubmit = async (e) => {
    e.preventDefault();
    try {
      setSavingOrderReview(true);
      await createOrderReview({
        orderId,
        rating: orderRating,
        comment: orderComment,
      }).unwrap();

      toast.success("Order review submitted", {autoClose: 1000});
      setOrderRating(0);
      setOrderComment("");
      refetch();
      refetchGetOrderReview();
    } catch (err) {
      toast.error(err?.data?.message || err.error, {autoClose: 2000});
    } finally {
      setSavingOrderReview(false);
    }
  };

  if (isLoadingGetOrderReview) return <Loader />;

  return (
    <>
      {orderReview ? (
        <ListGroup.Item className="white-section">
          <h2>Order Review</h2>
          <Rating
            value={orderReview.rating}
            text={`reviewed on ${dateFormatter(orderReview.createdAt)}`}
          />
          <p>{orderReview.comment}</p>
        </ListGroup.Item>
      ) : !order.delivery.isDelivered ? (
        <ListGroup.Item>
          <h2>Order Review</h2>
          <Message variant="info">
            Review can be provided after order delivery
          </Message>
        </ListGroup.Item>
      ) : Date.now() >
        new Date(order.delivery.deliveredAt).getTime() +
          14 * 24 * 60 * 60 * 1000 ? (
        <ListGroup.Item>
          <h2>Order Review</h2>
          <Message variant="info">
            Review window expired (14 days past order delivery)
          </Message>
        </ListGroup.Item>
      ) : (
        <ListGroup.Item>
          <h2>Write Order Review</h2>
          {isLoadingOrderReview && <Loader />}
          <Form onSubmit={handleOrderReviewSubmit}>
            <Form.Group controlId="orderRating" className="my-2">
              <Form.Label>Rating</Form.Label>
              <Form.Control
                as="select"
                value={orderRating}
                onChange={(e) => setOrderRating(Number(e.target.value))}
              >
                <option value="">Select rating</option>
                <option value="1">1 - Poor</option>
                <option value="2">2 - Fair</option>
                <option value="3">3 - Good</option>
                <option value="4">4 - Very Good</option>
                <option value="5">5 - Excellent</option>
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="orderComment" className="my-2">
              <Form.Label>Comment</Form.Label>
              <Form.Control
                as="textarea"
                rows="3"
                value={orderComment}
                onChange={(e) => setOrderComment(e.target.value)}
              />
            </Form.Group>

            <Button
              disabled={isLoadingOrderReview || savingOrderReview}
              type="submit"
              variant="primary"
            >
              {savingOrderReview ? (
                <>
                  <Spinner animation="border" size="sm" /> Submitting...
                </>
              ) : (
                "Submit Order Review"
              )}
            </Button>
          </Form>
        </ListGroup.Item>
      )}
    </>
  );
};

export default OrderReview;